/**
 * The 38 SVG shell glyphs (ui-ux §20.1 family A; art §15.1 "System glyphs"). Owner: UI/Visual (F3).
 *
 * Controls only — never a game noun (art §15.1 rule 5). 24 grid, 2 px `currentColor` stroke, round caps and joins,
 * ≤ 1 flat fill. One inline sprite is injected once ({@link ensureShellSprite}); {@link glyph} returns
 * `<svg class="mc-glyph"><use href="#i-…"/></svg>`.
 */

export const SHELL_GLYPHS = [
  'back', 'close', 'more', 'info', 'warning', 'lock', 'check', 'cross', 'plus', 'minus', 'chevron-left', 'chevron-right',
  'chevron-down', 'search', 'refresh', 'settings', 'undo', 'redo', 'random-dice', 'eye-compare', 'rotate', 'zoom-in',
  'zoom-out', 'view-snap', 'pause', 'play', 'speed-2', 'skip', 'share', 'copy', 'paste', 'filter', 'sort', 'sound-on',
  'sound-off', 'vibration', 'text-size', 'motion',
] as const;
export type ShellGlyph = typeof SHELL_GLYPHS[number];

function gear(): string {
  const teeth = 8, ro = 9.6, ri = 7.4, pts: string[] = [];
  for (let i = 0; i < teeth; i++) {
    const a = (i / teeth) * Math.PI * 2;
    const w = Math.PI / teeth * 0.55;
    for (const [ang, r] of [[a - w * 1.25, ri], [a - w * 0.7, ro], [a + w * 0.7, ro], [a + w * 1.25, ri]] as const) {
      pts.push(`${(12 + r * Math.cos(ang)).toFixed(2)} ${(12 + r * Math.sin(ang)).toFixed(2)}`);
    }
  }
  return `<path d="M${pts.join('L')}Z"/><circle cx="12" cy="12" r="3"/>`;
}

/** Path bodies on the 24 grid (stroke = currentColor unless a `fill` is given). */
const BODIES: Record<ShellGlyph, string> = {
  'back': '<path d="M19 12H5M11 6L5 12L11 18"/>',
  'close': '<path d="M6 6L18 18M18 6L6 18"/>',
  'more': '<circle cx="6" cy="12" r="1.5" fill="currentColor" stroke="none"/><circle cx="12" cy="12" r="1.5" fill="currentColor" stroke="none"/><circle cx="18" cy="12" r="1.5" fill="currentColor" stroke="none"/>',
  'info': '<circle cx="12" cy="12" r="9"/><path d="M12 11V16.5"/><circle cx="12" cy="7.8" r="1.2" fill="currentColor" stroke="none"/>',
  'warning': '<path d="M12 3.5L21.5 20H2.5Z"/><path d="M12 10V14"/><circle cx="12" cy="17" r="1.1" fill="currentColor" stroke="none"/>',
  'lock': '<rect x="5" y="11" width="14" height="9.5" rx="2"/><path d="M8 11V8A4 4 0 0 1 16 8V11"/>',
  'check': '<path d="M5 12.5L10 17.5L19 7"/>',
  'cross': '<circle cx="12" cy="12" r="9"/><path d="M8.5 8.5L15.5 15.5M15.5 8.5L8.5 15.5"/>',
  'plus': '<path d="M12 5V19M5 12H19"/>',
  'minus': '<path d="M5 12H19"/>',
  'chevron-left': '<path d="M15 5L8 12L15 19"/>',
  'chevron-right': '<path d="M9 5L16 12L9 19"/>',
  'chevron-down': '<path d="M5 9L12 16L19 9"/>',
  'search': '<circle cx="10.5" cy="10.5" r="6"/><path d="M15 15L20 20"/>',
  'refresh': '<path d="M20 12A8 8 0 1 1 17.66 6.34"/><path d="M20 4V9H15"/>',
  'settings': gear(),
  'undo': '<path d="M9 14L4 9L9 4"/><path d="M4 9H14.5A5.5 5.5 0 0 1 14.5 20H11"/>',
  'redo': '<path d="M15 14L20 9L15 4"/><path d="M20 9H9.5A5.5 5.5 0 0 0 9.5 20H13"/>',
  'random-dice': '<rect x="4" y="4" width="16" height="16" rx="3"/><circle cx="8.5" cy="8.5" r="1.3" fill="currentColor" stroke="none"/><circle cx="12" cy="12" r="1.3" fill="currentColor" stroke="none"/><circle cx="15.5" cy="15.5" r="1.3" fill="currentColor" stroke="none"/>',
  'eye-compare': '<path d="M2 12C5 6.5 8.5 4.5 12 4.5S19 6.5 22 12C19 17.5 15.5 19.5 12 19.5S5 17.5 2 12Z"/><circle cx="12" cy="12" r="3"/><path d="M12 2V4M12 20V22"/>',
  'rotate': '<path d="M3.5 12C3.5 9.5 7.3 7.5 12 7.5S20.5 9.5 20.5 12S16.7 16.5 12 16.5H10"/><path d="M12.5 13.5L9.5 16.5L12.5 19.5"/>',
  'zoom-in': '<circle cx="10.5" cy="10.5" r="6"/><path d="M15 15L20 20M10.5 8V13M8 10.5H13"/>',
  'zoom-out': '<circle cx="10.5" cy="10.5" r="6"/><path d="M15 15L20 20M8 10.5H13"/>',
  'view-snap': '<path d="M4 9V4H9M15 4H20V9M20 15V20H15M9 20H4V15"/><circle cx="12" cy="12" r="2.5"/>',
  'pause': '<rect x="6.5" y="5" width="3.5" height="14" rx="1" fill="currentColor" stroke="none"/><rect x="14" y="5" width="3.5" height="14" rx="1" fill="currentColor" stroke="none"/>',
  'play': '<path d="M8 5L19 12L8 19Z" fill="currentColor" stroke="none"/>',
  'speed-2': '<path d="M3.5 6L11.5 12L3.5 18Z M12.5 6L20.5 12L12.5 18Z" fill="currentColor" stroke="none"/>',
  'skip': '<path d="M5 5.5L14.5 12L5 18.5Z" fill="currentColor" stroke="none"/><path d="M18.5 5V19"/>',
  'share': '<circle cx="18" cy="5.5" r="2.5"/><circle cx="6" cy="12" r="2.5"/><circle cx="18" cy="18.5" r="2.5"/><path d="M8.2 10.8L15.8 6.7M8.2 13.2L15.8 17.3"/>',
  'copy': '<rect x="8" y="8" width="12" height="12" rx="2"/><path d="M16 8V6A2 2 0 0 0 14 4H6A2 2 0 0 0 4 6V14A2 2 0 0 0 6 16H8"/>',
  'paste': '<rect x="5" y="5" width="14" height="16" rx="2"/><path d="M9 5V3.5H15V5"/><path d="M9 11H15M9 15H13"/>',
  'filter': '<path d="M3.5 5H20.5L14 12.5V19L10 21V12.5Z"/>',
  'sort': '<path d="M7 4V20M3.5 16.5L7 20L10.5 16.5M17 20V4M13.5 7.5L17 4L20.5 7.5"/>',
  'sound-on': '<path d="M4 9H8L13 5V19L8 15H4Z"/><path d="M16.5 9A4 4 0 0 1 16.5 15M19 6.5A8 8 0 0 1 19 17.5"/>',
  'sound-off': '<path d="M4 9H8L13 5V19L8 15H4Z"/><path d="M16.5 9.5L21.5 14.5M21.5 9.5L16.5 14.5"/>',
  'vibration': '<rect x="8" y="4" width="8" height="16" rx="2"/><path d="M4.5 8.5V15.5M19.5 8.5V15.5M2 10.5V13.5M22 10.5V13.5"/>',
  'text-size': '<path d="M2.5 19L7.5 6L12.5 19M4.4 14.5H10.6"/><path d="M14 19L17.5 11L21 19M15.2 16.4H19.8"/>',
  'motion': '<path d="M3 8H10M2 12H8.5M3 16H10"/><circle cx="16" cy="12" r="4.5"/>',
};

const SPRITE_ID = 'mc-shell-sprite';

/** The sprite markup (symbols `#i-<name>`). */
export function shellSpriteMarkup(): string {
  const attrs = 'fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"';
  const symbols = SHELL_GLYPHS.map((n) => `<symbol id="i-${n}" viewBox="0 0 24 24" ${attrs}>${BODIES[n]}</symbol>`).join('');
  return `<svg xmlns="http://www.w3.org/2000/svg" id="${SPRITE_ID}" aria-hidden="true" style="position:absolute;width:0;height:0;overflow:hidden">`
    + `${symbols}</svg>`;
}

/** Injects the sprite into `<body>` once. */
export function ensureShellSprite(doc: Document = document): void {
  if (doc.getElementById(SPRITE_ID)) return;
  const host = doc.createElement('div');
  host.innerHTML = shellSpriteMarkup();
  doc.body.prepend(host.firstChild as Element);
}

/** An inline shell glyph (`size` CSS px, default 24; 20–24 per art §15.1). Decorative unless `label` is given. */
export function glyph(name: ShellGlyph, opts: { size?: number; label?: string; class?: string } = {}): SVGSVGElement {
  ensureShellSprite();
  const ns = 'http://www.w3.org/2000/svg';
  const el = document.createElementNS(ns, 'svg');
  const size = opts.size ?? 24;
  el.setAttribute('class', `mc-glyph${opts.class ? ` ${opts.class}` : ''}`);
  el.setAttribute('width', String(size));
  el.setAttribute('height', String(size));
  el.setAttribute('viewBox', '0 0 24 24');
  el.dataset.glyph = name;
  if (opts.label) { el.setAttribute('role', 'img'); el.setAttribute('aria-label', opts.label); } else el.setAttribute('aria-hidden', 'true');
  const use = document.createElementNS(ns, 'use');
  use.setAttribute('href', `#i-${name}`);
  el.appendChild(use);
  return el;
}
